import { all, put, takeEvery } from "@redux-saga/core/effects";
import {
  addRolesToWorkforce,
  assignProcedureToWorkforce,
  createNewWorkforce,
  // getAveragePercentageOfTotalWorkforceSummary,
  // getAveragePercentageOfWorkforceSummaryForEachRoles,
  getOperations,
  getRolesData,
  updateConnectedLocation,
  unarchiveWorkforce,
  updateWorkforce
} from "../../Services/database";
import { isValidArray, isValidObject } from "../../Services/validators";
import { setErrorStatus, setSuccessStatus } from "../status/action";
import store from "../store";
import { addRolesToSelectedEmployee } from "./action";
import { getSummaryCount } from "../../Services/api";
import { getDatesInRange } from "../../Utils/Constants/Constants";

export const actionTypes = {
  ADD_CAPTURE_SUMMARY_DATA: "ADD_CAPTURE_SUMMARY_DATA",
  ADD_ROLES_DATA: "ADD_ROLES_DATA",
  CREATE_WORKFORCE: "CREATE_WORKFORCE",
  EDIT_WORKFORCE: "EDIT_WORKFORCE",
  ARCHIVE_WORKFORCE: "ARCHIVE_WORKFORCE",
  UNARCHIVE_WORKFORCE: "UNARCHIVE_WORKFORCE",
  GET_ROLES_DATA: "GET_ROLES_DATA",
  GET_ALL_WORKFORCE_SUMMARY_DATA: "GET_ALL_WORKFORCE_SUMMARY_DATA",
  SET_WORKFORCE_DATA: "SET_WORKFORCE_DATA",
  SET_WORKFORCE_RESULTS: "SET_WORKFORCE_RESULTS",
  ASSIGN_LOCATIONS: "ASSIGN_LOCATIONS",
  UPDATE_PROCEDURES_TO_WORKFORCE: "UPDATE_PROCEDURES_TO_WORKFORCE",
  GET_OPERATIONS_DATA: "GET_OPERATIONS_DATA"
};

function* setWorkforceDataWorker(action) {
  try {
    yield setWorkforceLoading(true);

    // archived employees live in archivedData so every other consumer of
    // workforce.data keeps seeing active employees only
    const data = {};
    const archivedData = {};
    Object.entries(action.payload.data || {}).forEach(([id, employee]) => {
      if (employee?.actions?.deletedAt) {
        archivedData[id] = employee;
      } else {
        data[id] = employee;
      }
    });

    yield put({
      type: "PUT_WORKFORCE_DATA",
      payload: {
        data: data,
        archivedData: archivedData
      }
    });
    yield setWorkforceLoading(false);
  } catch (error) {
    yield setWorkforceLoading(false);
    console.error(error);
  }
}

function* setWorkforceResultsWorker(action) {
  try {
    yield setWorkforceLoading(true);

    yield put({
      type: "PUT_WORKFORCE_RESULTS",
      payload: {
        data: action.payload.data
      }
    });

    yield put({
      type: "SET_WORKFORCE_COUNT",
      payload: {
        count: action.payload.count
      }
    });
    yield setWorkforceLoading(false);
  } catch (error) {
    yield setWorkforceLoading(false);
    console.error(error);
  }
}

function* setCaptureSummaryDataWorker(action) {
  yield put({
    type: "SET_CAPTURE_SUMMARY_DATA",
    payload: {
      data: action.payload.data
    }
  });
}

function* getRolesDataWorker() {
  try {
    yield setWorkforceLoading(true);
    const data = yield getRolesData();
    yield put({
      type: "SET_ROLES_DATA",
      payload: {
        data: data
      }
    });
    yield setWorkforceLoading(false);
  } catch (error) {
    yield setWorkforceLoading(false);
    setErrorStatus(error);
    console.error(error, "getRolesDataWorker");
  }
}

function* getAllWorkforceSummaryDataWorker() {
  try {
    yield setWorkforceLoading(true);

    const roles = store.getState().workforce.roles;
    if (!isValidObject(roles)) {
      return;
    }

    let operationsData = store.getState().workforce?.operations;
    if (!operationsData) {
      operationsData = yield getOperations();
      yield put({
        type: "SET_OPERATIONS_DATA",
        payload: {
          data: operationsData
        }
      });
    }
    if (!operationsData) {
      return;
    }

    const filterDateRange = store.getState().table.workforce.filter.dateRange;

    const dateRange = getDatesInRange({
      from: +new Date(filterDateRange.from),
      to: +new Date(filterDateRange.to)
    });

    const filteredRoles =
      isValidObject(roles) &&
      Object.values(roles).filter((role) =>
        isValidObject(role.attendance?.capture)
      );

    if (!isValidArray(dateRange) || !isValidArray(filteredRoles)) {
      return;
    }

    let summaryCount = {};

    for (const date of dateRange) {
      for (const role of filteredRoles) {
        let shiftCounts = [];

        let shiftStartTime = null;
        for (const [index, shift] of operationsData?.shifts?.entries() || []) {
          const [startHour, startMin] = shift.from.split(":").map(Number);
          const [endHour, endMin] = shift.to.split(":").map(Number);

          if (index === 0) {
            shiftStartTime = new Date(date.from);
            shiftStartTime.setHours(startHour, startMin, 0, 0);
          }

          let shiftEndTime = new Date(date.from);
          shiftEndTime.setHours(endHour, endMin, 59, 59);

          // KNOWN GAP (intentional, not an oversight): this is the one remaining
          // /summary ("uniqueCount") call in adminPanel. Firestore has no
          // distinct-count aggregation, and this per-shift breakdown is keyed by
          // config/operations.shifts — a config source independent of the
          // per-role interval data already denormalized onto attendanceSummary —
          // so it can't be answered from existing aggregations without adding a
          // new denormalized field (and backfilling historical attendanceSummary
          // docs against whatever operations.shifts looked like on each day,
          // which isn't tracked). Left on /summary until that's designed
          // properly; functions/summary.js stays deployed for this and for
          // app/exec, which is not part of this migration.
          const response = yield getSummaryCount({
            accessToken: store.getState().auth.data.accessToken,
            type: "uniqueCount",
            collection: "captures",
            uniqueKey: "employee.id",
            query: {
              count: [
                "createdAt",
                ">=",
                +shiftStartTime,
                "createdAt",
                "<=",
                +shiftEndTime,
                "employee.roles",
                "array-contains",
                role.documentId,
                "success",
                "==",
                true
              ]
            }
          });

          shiftCounts.push({
            title: `${role.documentId}-${index + 1}`,
            count: response.count
          });
        }

        for (let index = shiftCounts?.length - 1; index >= 0; index--) {
          const currentData = shiftCounts[index];
          const previousData = shiftCounts[index - 1];

          const currentShiftCount = previousData
            ? currentData.count - previousData.count
            : currentData.count;

          if (summaryCount[currentData.title]) {
            summaryCount[currentData.title] += currentShiftCount;
          } else {
            summaryCount[currentData.title] = currentShiftCount;
          }
        }
      }
    }

    let summaryData = {};

    let totalSummary = {};

    for (const key of Object.keys(summaryCount)) {
      const [roleId, shiftNo] = key.split("-");

      const shift = `Shift ${shiftNo}`;

      if (summaryData[roleId]) {
        summaryData[roleId] = {
          ...summaryData[roleId],
          count: {
            ...summaryData[roleId].count,
            [shift]: summaryCount[key]
          }
        };
      } else {
        summaryData[roleId] = {
          title: roles[roleId]?.name?.toUpperCase(),
          count: {
            [shift]: summaryCount[key]
          }
        };
      }

      if (totalSummary[shift]) {
        totalSummary[shift] += summaryCount[key];
      } else {
        totalSummary[shift] = summaryCount[key];
      }
    }

    summaryData.totalSummary = {
      title: "OVERALL",
      count: totalSummary
    };

    yield put({
      type: "SET_WORKFORCE_SUMMARY_DATA",
      payload: { data: Object.values(summaryData) }
    });

    yield setWorkforceLoading(false);
  } catch (error) {
    yield setWorkforceLoading(false);
    setErrorStatus(error);
    console.error(error, "getAllWorkforceSummaryDataWorker");
  }
}

function* addRolesDataWorker(action) {
  try {
    yield setWorkforceLoading(true);
    yield addRolesToWorkforce(action.payload.documentId, action.payload.roles);
    yield setSuccessStatus("Roles Updated successfully");
    yield setWorkforceLoading(false);
  } catch (error) {
    yield setWorkforceLoading(false);
    setErrorStatus(error);
    console.error(error, "addRolesDataWorker");
  }
}

function* createWorkforceWorker(action) {
  try {
    yield setWorkforceLoading(true);

    const { data } = action.payload;

    const existingWorkforce = store.getState().workforce.data;
    if (isValidObject(existingWorkforce)) {
      const employeeExists = Object.values(existingWorkforce).some(
        (employee) =>
          (data.type === "admin" && employee.email === data.email) ||
          (data.type !== "admin" && employee.phoneNumber === data.phoneNumber)
      );

      if (employeeExists) {
        yield setErrorStatus({
          code: "custom",
          message: "Workforce already exists."
        });
        yield setWorkforceLoading(false);
        return;
      }

      if (existingWorkforce[data.employeeId]) {
        yield setErrorStatus({
          code: "custom",
          message: `${data.type} ID already exists.`
        });
        yield setWorkforceLoading(false);
        return;
      }
    }

    yield createNewWorkforce(data);

    // if (action.payload.file) {
    //   createPicture(
    //     {
    //       name: data.name,
    //       employee: {
    //         phoneNumber: data.phoneNumber,
    //         name: data.name,
    //         id: documentId
    //       }
    //     },
    //     file
    //   );
    // }
    yield setSuccessStatus("Workforce created successfully");

    yield setWorkforceLoading(false);
  } catch (error) {
    yield setWorkforceLoading(false);
    yield setErrorStatus(error.message);
  }
}

function* editWorkforceWorker(action) {
  try {
    yield setWorkforceLoading(true);

    const { data } = action.payload;

    console.log("edit Data:", data)

    const selectedEmployee = store.getState().table.workforce.selectedEmployee;

    if (data.roles) {
      yield addRolesToSelectedEmployee(selectedEmployee, data.roles);
    }

    if (data.name || data.phoneNumber){
      yield updateWorkforce(data.documentId, {
        ...(data.name ? {name: data.name} : {}),
        ...(data.phoneNumber ? {phoneNumber: data.phoneNumber} : {}),
      });
    }

    // if (data.file && !data.referencePictureDocumentId) {
    //   createPicture(
    //     {
    //       name: data.name,
    //       employee: {
    //         phoneNumber: data.phoneNumber,
    //         name: data.name,
    //         id: data.documentId
    //       }
    //     },
    //     data.file
    //   );
    // }

    // if (data.file && data.referencePictureDocumentId) {
    //   editPicture(
    //     {
    //       name: data.name,
    //       employee: {
    //         phoneNumber: data.phoneNumber,
    //         name: data.name,
    //         id: data.documentId
    //       }
    //     },
    //     data.file,
    //     data.referencePictureDocumentId
    //   );
    // }

    yield setSuccessStatus("Workforce updated successfully");

    yield setWorkforceLoading(false);
  } catch (error) {
    yield setWorkforceLoading(false);
    yield setErrorStatus(error.message);
  }
}

function* archiveWorkforceWorker(action) {
  try {
    yield setWorkforceLoading(true);

    yield updateWorkforce(action.payload.documentId, {
      "actions.deletedAt": Date.now()
    });

    yield setSuccessStatus("Account archived successfully");

    yield setWorkforceLoading(false);
  } catch (error) {
    yield setWorkforceLoading(false);
    yield setErrorStatus(error.message);
  }
}

function* unarchiveWorkforceWorker(action) {
  try {
    yield setWorkforceLoading(true);

    yield unarchiveWorkforce(action.payload.documentId);

    yield setSuccessStatus("Account unarchived successfully");

    yield setWorkforceLoading(false);
  } catch (error) {
    yield setWorkforceLoading(false);
    yield setErrorStatus(error.message);
  }
}

function* assignLocationsToEmployee(action) {
  try {
    yield setWorkforceLoading(true);

    const employeeData = store.getState().workforce.data[
      action.payload.employeeId
    ];

    yield updateConnectedLocation(action.payload.employeeId, {
      procedure: employeeData?.connected?.procedure || [],
      location: action.payload.locations
    });

    yield setSuccessStatus("Locations updated successfully");
  } catch (error) {
    yield setWorkforceLoading(false);
    setErrorStatus(error, "from assign location");
  }
}

function* assignProceduresToEmployee(action) {
  try {
    yield setWorkforceLoading(true);

    const employeeId = action.payload.employeeId;
    const employeeData = store.getState().workforce.data[employeeId];

    const procedures = action.payload.procedures;

    const procedureIds = procedures.map((p) => p.procedureId);

    yield assignProcedureToWorkforce(employeeId, {
      linkedProcedures: procedures,
      connected: {
        location: employeeData?.connected?.location || [],
        procedure: procedureIds
      }
    });

    yield setSuccessStatus("Procedure updated successfully");
  } catch (error) {
    yield setWorkforceLoading(false);
    setErrorStatus(error, "from assign location");
  }
}

function* getOperationsDataWorker() {
  try {
    yield setWorkforceLoading(true);
    const data = yield getOperations();
    yield put({
      type: "SET_OPERATIONS_DATA",
      payload: {
        data: data
      }
    });
    yield setWorkforceLoading(false);
  } catch (error) {
    yield setWorkforceLoading(false);
    setErrorStatus(error);
    console.error(error, "getOperationsDataWorker");
  }
}

export default function* workforceWatcher() {
  yield all([
    takeEvery("ADD_CAPTURE_SUMMARY_DATA", setCaptureSummaryDataWorker),
    takeEvery("ADD_ROLES_DATA", addRolesDataWorker),

    takeEvery("CREATE_WORKFORCE", createWorkforceWorker),
    takeEvery("EDIT_WORKFORCE", editWorkforceWorker),
    takeEvery("ARCHIVE_WORKFORCE", archiveWorkforceWorker),
    takeEvery("UNARCHIVE_WORKFORCE", unarchiveWorkforceWorker),
    takeEvery("GET_ROLES_DATA", getRolesDataWorker),
    takeEvery(
      "GET_ALL_WORKFORCE_SUMMARY_DATA",
      getAllWorkforceSummaryDataWorker
    ),
    takeEvery("SET_WORKFORCE_DATA", setWorkforceDataWorker),
    takeEvery("SET_WORKFORCE_RESULTS", setWorkforceResultsWorker),
    takeEvery("ASSIGN_LOCATIONS", assignLocationsToEmployee),
    takeEvery("UPDATE_PROCEDURES_TO_WORKFORCE", assignProceduresToEmployee),
    takeEvery("GET_OPERATIONS_DATA", getOperationsDataWorker)
  ]);
}

function* setWorkforceLoading(bool) {
  yield put({
    type: "SET_WORKFORCE_LOADING",
    payload: {
      loading: bool
    }
  });
}
